/**
 * InputController: gathers keyboard + mouse input into a CameraFrame.
 *
 * Simplified from supersplat-viewer's KeyboardMouseDevice/InputController.
 * Single device (keyboard + mouse), pointer-lock based.
 *
 * move output: [strafe, verticalOrJump, forward]
 *   - fly mode: vertical = (E - Q), scaled by Shift/Ctrl factor
 *   - walk mode: move[1] = 1 when Space held (jump flag), else 0
 *   - horizontal is normalized then scaled by Shift/run-toggle (x2.2) / Ctrl (x0.5)
 * rotate output: [yawDelta, pitchDelta, 0] in degrees, from mouse movement.
 */

import { CameraFrame } from '../player/types';

export type ControlMode = 'fly' | 'walk';

interface TouchPoint {
    x: number;
    y: number;
    width: number;
    height: number;
}

function isEditableTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    const tag = target.tagName;
    return target.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

export function isVirtualLandscapeRuntime(): boolean {
    return document.getElementById('app-root')?.classList.contains('mobile-virtual-landscape') ??
        (window.matchMedia('(pointer: coarse)').matches && window.innerHeight > window.innerWidth);
}

export function toVirtualLandscapePoint(e: { clientX: number; clientY: number }): { x: number; y: number; width: number; height: number } {
    const virtualLandscape = isVirtualLandscapeRuntime();
    if (!virtualLandscape) {
        return { x: e.clientX, y: e.clientY, width: window.innerWidth, height: window.innerHeight };
    }
    return {
        x: e.clientY,
        y: window.innerWidth - e.clientX,
        width: window.innerHeight,
        height: window.innerWidth
    };
}

class InputController {
    readonly frame = new CameraFrame();

    /** Mouse look sensitivity in degrees per pixel. */
    mouseSensitivity = 0.1;

    private _keys = new Set<string>();
    private _mode: ControlMode = 'fly';
    private _canvas: HTMLCanvasElement;
    /** Whether left mouse button is currently held down. */
    private _mouseDown = false;
    private _lastMouseX = 0;
    private _lastMouseY = 0;
    private _touchMoveHud: HTMLElement;
    private _touchMoveThumb: HTMLElement;
    private _touchLookHud: HTMLElement;
    private _touchLookThumb: HTMLElement;
    private _touchMovePointer: number | null = null;
    private _touchLookPointer: number | null = null;
    private _touchMoveStartX = 0;
    private _touchMoveStartY = 0;
    private _touchLookStartX = 0;
    private _touchLookStartY = 0;
    private _touchMoveX = 0;
    private _touchMoveY = 0;
    private _touchLookX = 0;
    private _touchLookY = 0;
    private readonly _touchMoveRadius = 40;
    private readonly _touchLookRadius = 40;
    private readonly _touchLookSpeed = 150;
    /** Persistent run toggle (on-screen 跑/走 button). Combines with hold-Shift. */
    private _runToggle = false;
    /** One-shot jump requested by the on-screen 跳跃 button (consumed next update). */
    private _jumpQueued = false;

    constructor(canvas: HTMLCanvasElement) {
        this._canvas = canvas;
        this._touchMoveHud = this._buildTouchHud('move');
        this._touchMoveThumb = this._touchMoveHud.querySelector('#mobile-move-thumb') as HTMLElement;
        this._touchLookHud = this._buildTouchHud('look');
        this._touchLookThumb = this._touchLookHud.querySelector('#mobile-look-thumb') as HTMLElement;
        window.addEventListener('keydown', this._onKeyDown);
        window.addEventListener('keyup', this._onKeyUp);
        // Mouse look: rotate camera while left button is held.
        canvas.addEventListener('mousemove', this._onMouseMove);
        canvas.addEventListener('mousedown', this._onMouseDown);
        window.addEventListener('mouseup', this._onMouseUp);
        // Prevent context menu so right-click doesn't interrupt.
        canvas.addEventListener('contextmenu', (e) => e.preventDefault());
        canvas.addEventListener('pointerdown', this._onPointerDown);
        canvas.addEventListener('pointermove', this._onPointerMove);
        canvas.addEventListener('pointerup', this._onPointerUp);
        canvas.addEventListener('pointercancel', this._onPointerUp);
    }

    set mode(value: ControlMode) {
        this._mode = value;
    }

    get mode(): ControlMode {
        return this._mode;
    }

    /** Toggle persistent run (on-screen button). Stack with hold-Shift. */
    setRunToggle(v: boolean): void { this._runToggle = v; }
    get runToggle(): boolean { return this._runToggle; }

    /** Request a single jump from the on-screen button (edge-triggered). */
    requestJump(): void { this._jumpQueued = true; }

    /** Read key state for control keys (case-insensitive). */
    private _down(code: string): boolean {
        return this._keys.has(code);
    }

    private _onKeyDown = (e: KeyboardEvent) => {
        if (isEditableTarget(e.target)) return;
        this._keys.add(e.code);
        // Stop default browser scrolling for space / arrows.
        if (e.code === 'Space' || e.code.startsWith('Arrow')) {
            e.preventDefault();
        }
    };

    private _onKeyUp = (e: KeyboardEvent) => {
        this._keys.delete(e.code);
    };

    private _onMouseDown = (e: MouseEvent) => {
        if (e.button !== 0) return;
        this._mouseDown = true;
        this._lastMouseX = e.clientX;
        this._lastMouseY = e.clientY;
    };

    private _onMouseUp = (e: MouseEvent) => {
        if (e.button !== 0) return;
        this._mouseDown = false;
    };

    private _onMouseMove = (e: MouseEvent) => {
        if (!this._mouseDown) return;
        // Still support pointer-lock movementX/Y if available, otherwise fallback to clientX/Y delta.
        if (document.pointerLockElement != null) {
            this.frame.appendRotate(
                e.movementX * this.mouseSensitivity,
                e.movementY * this.mouseSensitivity,
                0
            );
        } else {
            const dx = e.clientX - this._lastMouseX;
            const dy = e.clientY - this._lastMouseY;
            this._lastMouseX = e.clientX;
            this._lastMouseY = e.clientY;
            this.frame.appendRotate(
                dx * this.mouseSensitivity,
                dy * this.mouseSensitivity,
                0
            );
        }
    };

    private _onPointerDown = (e: PointerEvent) => {
        if (e.pointerType !== 'touch') return;
        if (isEditableTarget(e.target)) return;
        e.preventDefault();
        this._canvas.setPointerCapture(e.pointerId);
        const point = toVirtualLandscapePoint(e);
        if (point.x < point.width * 0.5 && this._touchMovePointer == null) {
            this._touchMovePointer = e.pointerId;
            this._touchMoveStartX = point.x;
            this._touchMoveStartY = point.y;
            this._touchMoveX = 0;
            this._touchMoveY = 0;
            this._touchMoveHud.classList.add('active');
            this._updateTouchStick(this._touchMoveThumb, 0, 0);
            return;
        }
        if (this._touchLookPointer == null) {
            this._touchLookPointer = e.pointerId;
            this._touchLookStartX = point.x;
            this._touchLookStartY = point.y;
            this._touchLookX = 0;
            this._touchLookY = 0;
            this._touchLookHud.classList.add('active');
            this._updateTouchStick(this._touchLookThumb, 0, 0);
        }
    };

    private _onPointerMove = (e: PointerEvent) => {
        if (e.pointerType !== 'touch') return;
        const point = toVirtualLandscapePoint(e);
        if (e.pointerId === this._touchMovePointer) {
            e.preventDefault();
            const dx = point.x - this._touchMoveStartX;
            const dy = point.y - this._touchMoveStartY;
            const len = Math.hypot(dx, dy);
            const scale = len > this._touchMoveRadius ? this._touchMoveRadius / len : 1;
            const stickX = dx * scale;
            const stickY = dy * scale;
            this._touchMoveX = stickX / this._touchMoveRadius;
            this._touchMoveY = -stickY / this._touchMoveRadius;
            this._updateTouchStick(this._touchMoveThumb, stickX, stickY);
            return;
        }
        if (e.pointerId === this._touchLookPointer) {
            e.preventDefault();
            const dx = point.x - this._touchLookStartX;
            const dy = point.y - this._touchLookStartY;
            const len = Math.hypot(dx, dy);
            const scale = len > this._touchLookRadius ? this._touchLookRadius / len : 1;
            const stickX = dx * scale;
            const stickY = dy * scale;
            this._touchLookX = stickX / this._touchLookRadius;
            this._touchLookY = stickY / this._touchLookRadius;
            this._updateTouchStick(this._touchLookThumb, stickX, stickY);
        }
    };

    private _onPointerUp = (e: PointerEvent) => {
        if (e.pointerType !== 'touch') return;
        if (e.pointerId === this._touchMovePointer) {
            this._touchMovePointer = null;
            this._touchMoveX = 0;
            this._touchMoveY = 0;
            this._touchMoveHud.classList.remove('active');
            this._updateTouchStick(this._touchMoveThumb, 0, 0);
        }
        if (e.pointerId === this._touchLookPointer) {
            this._touchLookPointer = null;
            this._touchLookX = 0;
            this._touchLookY = 0;
            this._touchLookHud.classList.remove('active');
            this._updateTouchStick(this._touchLookThumb, 0, 0);
        }
    };

    /** Build the move delta for this frame from held keys. */
    update(_dt: number) {
        const forward = (this._down('KeyW') ? 1 : 0) - (this._down('KeyS') ? 1 : 0) + this._touchMoveY;
        const strafe = (this._down('KeyD') ? 1 : 0) - (this._down('KeyA') ? 1 : 0) + this._touchMoveX;
        const upFly = (this._down('KeyE') ? 1 : 0) - (this._down('KeyQ') ? 1 : 0);
        // Jump: keyboard Space (hold) OR on-screen button tap (one-shot).
        const jump = this._down('Space') || this._jumpQueued;
        this._jumpQueued = false;

        const shift = this._down('ShiftLeft') || this._down('ShiftRight');
        const ctrl = this._down('ControlLeft') || this._down('ControlRight');
        // Run: hold Shift or use the persistent run-toggle button.
        const run = shift || this._runToggle;
        this.frame.setRunning(run);
        const factor = run ? 2.2 : (ctrl ? 0.5 : 1);

        // Normalize horizontal so diagonal isn't faster.
        let hx = strafe;
        let hz = forward;
        const hlen = Math.hypot(hx, hz);
        if (hlen > 0) {
            hx = (hx / hlen) * factor;
            hz = (hz / hlen) * factor;
        }

        // Fly vertical (E/Q) is fixed at 4 m/s, unaffected by run/ctrl factor.
        const vertical = this._mode === 'walk' ? (jump ? 1 : 0) : upFly;
        this.frame.appendMove(hx, vertical, hz);
        if (this._touchLookX !== 0 || this._touchLookY !== 0) {
            this.frame.appendRotate(this._touchLookX * this._touchLookSpeed * _dt, this._touchLookY * this._touchLookSpeed * _dt, 0);
        }
    }

    private _buildTouchHud(kind: 'move' | 'look'): HTMLElement {
        const root = document.createElement('div');
        root.id = kind === 'move' ? 'mobile-touch-controls' : 'mobile-look-controls';
        const stick = document.createElement('div');
        stick.id = kind === 'move' ? 'mobile-move-stick' : 'mobile-look-stick';
        const thumb = document.createElement('div');
        thumb.id = kind === 'move' ? 'mobile-move-thumb' : 'mobile-look-thumb';
        stick.appendChild(thumb);
        root.appendChild(stick);
        (document.getElementById('ui-layer') ?? document.body).appendChild(root);
        return root;
    }

    private _updateTouchStick(thumb: HTMLElement, x: number, y: number): void {
        thumb.style.transform = `translate(${x}px, ${y}px)`;
    }
}

export { InputController };
